import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { PrismBadge, PrismIcon } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import GeneralIconWithTooltip from 'lumen/components/general/icon-with-tooltip';
import SettingsMapping from 'lumen/components/settings/mapping';
import SettingsMappingDefinitionsMappingDefinitions from 'lumen/components/settings/mapping/definitions/mapping-definitions';
import SettingsMappingHierarchyMappingHierarchyBenchmark from 'lumen/components/settings/mapping/hierarchy/mapping-hierarchy-benchmark';
import SettingsMappingTableTableMappingBenchmark from 'lumen/components/settings/mapping/table/table-mapping-benchmark';
import type HotelSettingsMappingBenchmarkController from 'lumen/controllers/hotel/settings/mapping/benchmark';
import { PMS_MAPPING_TABS_BENCHMARK } from 'lumen/utils/constants/pms-mapping-constants';

interface HotelSettingsMappingBenchmarkSignature {
  Args: {
    controller: HotelSettingsMappingBenchmarkController;
    model: unknown;
  };
}

<template>
  {{! @glint-nocheck: Need to convert controller to *.ts }}
  <SettingsMapping
    @activeTab={{@controller.activeTab}}
    @getHierarchy={{@controller.getHierarchy}}
    @getMapping={{@controller.getMapping}}
    @isEndOfMapping={{@controller.isEndOfMapping}}
    @isLoading={{@controller.isLoading}}
    @isRouteLoading={{@controller.isRouteLoading}}
    @isShowingKpiSectionModal={{@controller.isShowingKpiSectionModal}}
    @isShowingMappingTypeModal={{@controller.isShowingMappingTypeModal}}
    @onChangeTab={{@controller.onChangeTab}}
    @tabs={{PMS_MAPPING_TABS_BENCHMARK}}
  >
    <:mappingProgress>
      <div class="mapping-progress flex items-center justify-end">
        {{#if @controller.isMappingDisabled}}
          <PrismBadge
            @iconName="visibility-on"
            @isSmall="true"
            @label={{t "benchmark_insight.pms_mapping.read_only_view"}}
            @type="neutral"
          />
        {{else}}
          <PrismIcon
            @color={{@controller.mappingProgressMessage.color}}
            @name={{if
              (eq @controller.mappingProgressMessage.color "warning")
              "circle-information-outline"
              "circle-checkmark"
            }}
          />

          <div
            class="{{if
                (eq @controller.mappingProgressMessage.color 'warning')
                'mapping-progress--warning'
                'mapping-progress--success'
              }}
              text-200--regular ml-200"
          >
            {{@controller.mappingProgressMessage.text}}
          </div>

          <div class="text-200--bold ml-400">
            {{t "benchmark_insight.pms_mapping.progress"}}
          </div>

          <div class="text-200--regular ml-100">
            {{@controller.formattedMappingProgress}}
          </div>

          <span class="ml-100">
            <GeneralIconWithTooltip
              @size="small"
              @tooltipContent={{t
                "benchmark_insight.pms_mapping.progress_message.info_tooltip"
              }}
            />
          </span>
        {{/if}}
      </div>
    </:mappingProgress>

    <:mappingHierarchy>
      <SettingsMappingHierarchyMappingHierarchyBenchmark
        @breakdown={{@controller.activeTab}}
        @isCategoryMappedToRows={{@controller.isCategoryMappedToRows}}
        @rootCategory={{@controller.model.hierarchy.last.value}}
      />
    </:mappingHierarchy>

    <:mappingTable>
      <SettingsMappingTableTableMappingBenchmark
        @breakdown={{@controller.activeTab}}
        @cancelMappingChanges={{@controller.cancelMappingChanges}}
        @getHierarchy={{@controller.getHierarchy}}
        @getMapping={{@controller.getMapping}}
        @isEditing={{@controller.isEditing}}
        @isMappingDisabled={{@controller.isMappingDisabled}}
        @patchMapping={{@controller.patchMappingTask}}
        @rootCategory={{@controller.model.hierarchy.last.value}}
        @rows={{@controller.model.mapping.last.value.mapping}}
        @showKpiSectionModal={{@controller.onShowKpiSectionModal}}
        @showMappingTypeModal={{fn
          (mut @controller.isShowingMappingTypeModal)
          true
        }}
        @startEditing={{@controller.startEditing}}
        @stopEditing={{@controller.stopEditing}}
      />
    </:mappingTable>
  </SettingsMapping>

  <SettingsMappingDefinitionsMappingDefinitions />
</template> satisfies TOC<HotelSettingsMappingBenchmarkSignature>;
